'use client';

import { EventClickElement } from '@tuic/tracking';
import { twJoin } from 'tailwind-merge';

import { Skeleton, Tag } from '../../../core-components';
import { Image } from '../Image';
import { MediaCarousel } from '../MediaCarousel';

import type { TagProps} from '../../../core-components';
import type { I18NFromComponent } from '../../../utils';
import type { Cta } from '../../../utils/cta';
import type { ImageProps } from '../Image';
import type { DyTrackingComponentProps } from '@tuic/lib/src/utils/create-dy-engagement-tracking-props';
import type { FC, ReactNode } from 'react';

type I18N = I18NFromComponent<typeof MediaCarousel>;

export type ContentCardImageProps = {
  images: ImageProps[];
  title?: ReactNode;
  cta?: Cta;
  tag?: TagProps;
  fallback?: boolean;
  forceHorizontalOnLarge?: boolean;
  i18n?: I18N;
} & DyTrackingComponentProps;

export const ContentCardImage: FC<ContentCardImageProps> = ({
  images,
  title,
  cta,
  tag,
  fallback,
  forceHorizontalOnLarge,
  i18n,
  dyTracking,
}) => {
  const imageClassName = twJoin(
    'aspect-4/3 @2xl:aspect-auto',
    forceHorizontalOnLarge && 'lg:aspect-auto',
  );

  if (fallback) {
    return <Skeleton className={imageClassName} />;
  }

  const ctaHref = cta && 'href' in cta ? cta.href : undefined;

  // Tag component positioned conditionally: bottom right for single images, bottom left for multiple images
  const tagElement = tag && (
    <div className="absolute z-10 bottom-2 left-2">
      <Tag {...tag} />
    </div>
  );

  // If there's a single image and an href, make it clickable
  if (images.length === 1 && ctaHref) {
    return (
      <>
        <a
          href={ctaHref}
          className="absolute inset-0 cursor-pointer"
          data-track-click-element={EventClickElement.Card}
          data-track-click-label={`image.${title}`}
          {...dyTracking}
        >
          <Image
            {...images[0]}
            className={twJoin(imageClassName, 'absolute inset-0')}
          />
        </a>
        {tagElement}
      </>
    );
  }

  // Non-clickable single image (original behavior)
  if (images.length === 1) {
    return (
      <>
        <Image
          {...images[0]}
          className={twJoin(imageClassName, 'absolute inset-0')}
        />
        {tagElement}
      </>
    );
  }

  return (
    <div className="absolute inset-0 isolate overflow-hidden">
      <MediaCarousel i18n={i18n}>
        {images.map((image, idx) => (
          <div key={idx} className="h-full">
            <Image {...image} className={twJoin(imageClassName, 'h-full')} />
          </div>
        ))}
      </MediaCarousel>
      {tagElement}
    </div>
  );
};
